import React, { createContext, useContext, useEffect, useRef } from 'react';
import type { PropsWithChildren } from 'react';

import { send } from 'loot-core/platform/client/connection';

import { useLocalPref } from '@desktop-client/hooks/useLocalPref';

type ScheduledAmountsContextValue = {
  showScheduled: boolean;
  toggleShowScheduled: () => void;
};

const ScheduledAmountsContext = createContext<
  ScheduledAmountsContextValue | undefined
>(undefined);

export function ScheduledAmountsProvider({ children }: PropsWithChildren) {
  const [showScheduled = false, setShowScheduled] = useLocalPref(
    'budget.showScheduled',
  );

  // Sync the initial state to the server on mount
  const initialSyncDone = useRef(false);
  useEffect(() => {
    if (!initialSyncDone.current) {
      initialSyncDone.current = true;
      if (showScheduled) {
        void send('budget/set-show-scheduled', { enabled: true });
      }
    }
  }, [showScheduled]);

  const toggleShowScheduled = () => {
    const newValue = !showScheduled;
    setShowScheduled(newValue);
    // Tell the server to include/exclude scheduled amounts in budget cells
    void send('budget/set-show-scheduled', { enabled: newValue });
  };

  return (
    <ScheduledAmountsContext.Provider
      value={{
        showScheduled,
        toggleShowScheduled,
      }}
    >
      {children}
    </ScheduledAmountsContext.Provider>
  );
}

export function useScheduledAmounts() {
  const context = useContext(ScheduledAmountsContext);
  if (!context) {
    throw new Error(
      'useScheduledAmounts must be used within a ScheduledAmountsProvider',
    );
  }
  return context;
}
